import { Injectable, OnDestroy, inject } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { BEBasicService } from "@services/be-basic.service";
import {
  Activity,
  ActivityStatusEnum,
} from "@services/business/_models/interfaces/activities.interfaces";
import { LegalRecordUI } from "@services/business/_models/interfaces/record.interfaces";
import { AccessRoleEnum } from "@services/business/_models/roles";
import { BeActivitiesService } from "@services/business/activities-services/be-activities.service";
import { BeUsersService } from "@services/business/user-services/be-users.service";
import { LCEventEnum } from "@services/ui-helpers/_models/interfaces/messaging.interfaces";
import {
  NotificationMessage,
  NotificationMessageReceiver,
} from "@services/ui-helpers/_models/interfaces/notifications.interfaces";
import { BENotificationsService } from "@services/ui-helpers/notifications-services/be-notifications.service";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { LegalRecordStateService } from "./legal-record-state.service";
import { MessagingStateService } from "./messaging-state.service";
import { processHttpError } from "./process-http-error";

export interface NavItem {
  displayName?: string;
  displayCollapsedName?: string;
  disabled?: boolean;
  iconName?: string | null;
  route?: string | null;
  queryParams?: Record<string, unknown>;
  children?: NavItem[];
  require?: AccessRoleEnum[];
  count?: number;
}

@Injectable({
  providedIn: "root",
})
export class MenuService implements OnDestroy, NotificationMessageReceiver {
  private readonly usersService = inject(BeUsersService);
  private readonly router = inject(Router);
  private readonly legalRecordStateService = inject(LegalRecordStateService);
  private readonly activitiesService = inject(BeActivitiesService);
  private readonly notificationsService = inject(BENotificationsService);
  private readonly basicService = inject(BEBasicService);
  private readonly messagingStateService = inject(MessagingStateService);

  private readonly menuSubject = new BehaviorSubject<NavItem[]>([]);
  private readonly routerSubscription: Subscription;
  private currentRecordInfo?: LegalRecordUI;
  private userActivitiesCount = 0;
  private unreadMessagesCount = 0;
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.usersService.loadRoles();

    this.updateMenu();
    this.initializeNotifications();
    this.initializeMessagingState();
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateMenu();
      });

    this.checkUserLoginAndInitialize();
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    this.menuSubject.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private checkUserLoginAndInitialize(): void {
    this.basicService
      .isUserLoggedIn()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (isLoggedIn) {
          this.initializeRecordInfo();
        }
      });
  }

  public getMenu(): Observable<NavItem[]> {
    return this.menuSubject.asObservable();
  }

  private updateMenu(): void {
    const isOnlyClient = this.usersService.isOnlyClient();

    const hasPlatformAdminRole =
      this.usersService.hasRole(AccessRoleEnum.PlatformAdmin) ||
      this.usersService.hasRole(AccessRoleEnum.PlatformSupport);

    let menuItems: NavItem[];

    if (isOnlyClient) {
      const recordId = this.getCurrentRecordId();
      const routes = this.getUserOnlyMenuItems(recordId);
      menuItems = this.getAccesibleRoutes(routes);
    } else if (hasPlatformAdminRole) {
      const routes = this.getPlatformAdminMenuItems();
      menuItems = this.getAccesibleRoutes(routes);
    } else {
      const routes = this.getMenuItems();
      menuItems = this.getAccesibleRoutes(routes);
    }

    this.menuSubject.next(menuItems);
  }

  private getAccesibleRoutes(routes: NavItem[]): NavItem[] {
    return routes
      .filter(route => {
        const roles = route.require ?? [];

        if (roles.length === 0) {
          return true;
        }

        return roles.some(role => this.usersService.hasRole(role));
      })
      .filter(
        route =>
          route?.route || (route?.children && route?.children?.length > 0)
      )
      .map(route => {
        return {
          ...route,
          children: route.children
            ? this.getAccesibleRoutes(route.children)
            : [],
        };
      });
  }

  private initializeRecordInfo(): void {
    this.legalRecordStateService.currentRecordInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe(record => {
        this.currentRecordInfo = record;
        this.updateMenu();
        this.refreshActivitiesCount();
        this.refreshMessagesCount();
      });
  }

  private initializeNotifications(): void {
    this.notificationsService.subscribeToNotifications(
      this,
      [
        LCEventEnum.NOTIF_RECORD_ACTIVITY_CREATED,
        LCEventEnum.NOTIF_RECORD_ACTIVITY_UPDATED,
        LCEventEnum.NOTIF_MSGBOX_MSG_RECEIVED,
      ],
      true
    );
  }

  private initializeMessagingState(): void {
    this.messagingStateService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.unreadMessagesCount = count;
        this.updateMenu();
      });
  }

  processNotification(notification: NotificationMessage): void {
    if (
      notification.event === LCEventEnum.NOTIF_RECORD_ACTIVITY_CREATED ||
      notification.event === LCEventEnum.NOTIF_RECORD_ACTIVITY_UPDATED
    ) {
      this.refreshActivitiesCount();
    }
    if (notification.event === LCEventEnum.NOTIF_MSGBOX_MSG_RECEIVED) {
      this.refreshMessagesCount();
    }
  }

  private refreshActivitiesCount(): void {
    this.activitiesService
      .getPendingActivities()
      .then((activities: Activity[]) => {
        const nonValidatedActivities = activities
          .filter(activity => activity.status !== ActivityStatusEnum.validated)
          .filter(
            activity => activity.recordId === this.currentRecordInfo?.recordId
          );
        this.userActivitiesCount = nonValidatedActivities.length;
        this.updateMenu();
      })
      .catch(error => {
        processHttpError(error);
      });
  }

  private refreshMessagesCount(): void {
    this.messagingStateService.checkUnreadMessages();
  }

  private getCurrentRecordId(): string | null {
    return this.legalRecordStateService.getCurrentRecordId();
  }

  private getUserOnlyMenuItems(recordId: string | null): NavItem[] {
    if (!recordId) {
      return [
        {
          children: [
            {
              displayName: "Menu.Messages",
              iconName: "message",
              route: "messaging",
              require: [AccessRoleEnum.User],
              count: this.unreadMessagesCount,
            },
          ],
          require: [AccessRoleEnum.User],
        },
      ];
    }

    return [
      {
        children: [
          {
            displayName: "Menu.Overview",
            iconName: "home",
            route: `records/${recordId}/overview`,
            require: [AccessRoleEnum.User],
          },
          {
            displayName: "Menu.LegalData",
            iconName: "user-circle-solid",
            route: `records/${recordId}/legal-data`,
            require: [AccessRoleEnum.User],
            count: this.currentRecordInfo?.summary?.["ldps"],
          },
          {
            displayName: "Menu.Documentation",
            iconName: "document-text",
            route: `records/${recordId}/documentation`,
            require: [AccessRoleEnum.User],
            count: this.currentRecordInfo?.summary?.["document"],
          },
          {
            displayName: "Menu.Activities",
            iconName: "clipboard-list",
            route: `records/${recordId}/activities`,
            require: [AccessRoleEnum.User],
            count: this.userActivitiesCount,
          },
          {
            displayName: "Menu.Messages",
            iconName: "message",
            route: "messaging",
            require: [AccessRoleEnum.User],
            count: this.unreadMessagesCount,
          },
          {
            displayName: "Menu.Payments",
            iconName: "cash",
            route: `records/${recordId}/payments`,
            require: [AccessRoleEnum.User],
            count: this.currentRecordInfo?.summary?.["payment"],
          },
          {
            displayName: "Menu.History",
            iconName: "document-search",
            route: `records/${recordId}/history`,
            require: [AccessRoleEnum.User],
          },
        ],
        require: [AccessRoleEnum.User],
      },
    ];
  }

  private getMenuItems(): NavItem[] {
    return [
      {
        children: [
          {
            displayName: "Menu.Dashboard",
            iconName: "home",
            route: "dashboard/view",
            require: [AccessRoleEnum.Lawyer, AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.PendingActions",
            iconName: "eye",
            route: "dashboard/operative/pending-actions",
            require: [AccessRoleEnum.Lawyer, AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.Records",
            iconName: "document-text",
            route: "dashboard/operative/records",
            require: [
              AccessRoleEnum.Lawyer,
              AccessRoleEnum.Admin,
              AccessRoleEnum.TenantAdmin,
              AccessRoleEnum.ChannelAdmin,
              AccessRoleEnum.ChannelEmployee,
            ],
          },
          {
            displayName: "Menu.Activities",
            iconName: "clipboard-list",
            route: "dashboard/operative/tasks",
            require: [AccessRoleEnum.Lawyer, AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.UserVisits",
            iconName: "uservisit",
            route: "dashboard/operative/uservisits",
            require: [AccessRoleEnum.Lawyer, AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.Messages",
            iconName: "message",
            route: "messaging",
            require: [
              AccessRoleEnum.User,
              AccessRoleEnum.Lawyer,
              AccessRoleEnum.Admin,
              AccessRoleEnum.TenantAdmin,
              AccessRoleEnum.ChannelAdmin,
              AccessRoleEnum.ChannelEmployee,
            ],
            count: this.unreadMessagesCount,
          },
        ],
        require: [
          AccessRoleEnum.User,
          AccessRoleEnum.Lawyer,
          AccessRoleEnum.Admin,
          AccessRoleEnum.TenantAdmin,
          AccessRoleEnum.ChannelAdmin,
          AccessRoleEnum.ChannelEmployee,
        ],
      },
      {
        children: [
          {
            displayName: "Menu.M2MUsers",
            iconName: "document-text",
            route: "channel/records",
            require: [AccessRoleEnum.ChannelM2M],
          },
          {
            displayName: "Menu.M2MRecords",
            iconName: "document",
            route: "channel/record-list",
            require: [AccessRoleEnum.ChannelM2M],
          },
        ],
        require: [AccessRoleEnum.ChannelM2M],
      },
      {
        displayName: "Menu.Management",
        displayCollapsedName: "Menu.ManagementCompressed",
        children: [
          {
            displayName: "Menu.Channels",
            iconName: "channels",
            route: "dashboard/management/channels",
            require: [AccessRoleEnum.Admin, AccessRoleEnum.ChannelAdmin],
          },
          {
            displayName: "Menu.Clients",
            iconName: "clients",
            route: "dashboard/management/clients",
            require: [AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.Lawyers",
            iconName: "lawyers",
            route: "dashboard/management/lawyers",
            require: [AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.Admins",
            iconName: "admins",
            route: "dashboard/management/admins",
            require: [AccessRoleEnum.TenantAdmin],
          },
        ],
        require: [
          AccessRoleEnum.Admin,
          AccessRoleEnum.TenantAdmin,
          AccessRoleEnum.ChannelAdmin,
        ],
      },
      {
        displayName: "Menu.Admin",
        children: [
          {
            displayName: "Menu.Reports",
            iconName: "chart-bar",
            route: "dashboard/reports",
            require: [
              AccessRoleEnum.Admin,
              AccessRoleEnum.TenantAdmin,
              AccessRoleEnum.ChannelAdmin,
              AccessRoleEnum.ChannelEmployee,
            ],
          },
          {
            displayName: "Menu.Payments",
            iconName: "cash",
            route: "dashboard/management/payments",
            require: [AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.DocumentTypes",
            iconName: "document-types",
            route: "dashboard/management/document-types",
            require: [AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.Templates",
            iconName: "templates",
            route: "dashboard/management/templates",
            require: [AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.LegalProcesses",
            iconName: "legal-processes",
            route: "dashboard/management/legal-processes",
            require: [AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.Tags",
            iconName: "solid-tag",
            route: "dashboard/management/tags",
            require: [AccessRoleEnum.Admin],
          },
          {
            displayName: "Menu.ActivitySubtypes",
            iconName: "activity-subtype",
            route: "dashboard/management/activities-subtypes",
            require: [AccessRoleEnum.TenantAdmin],
          },
        ],
        require: [
          AccessRoleEnum.Admin,
          AccessRoleEnum.TenantAdmin,
          AccessRoleEnum.ChannelAdmin,
          AccessRoleEnum.ChannelEmployee,
        ],
      },
    ];
  }

  private getPlatformAdminMenuItems(): NavItem[] {
    return [
      {
        displayName: "Menu.Control",
        children: [
          {
            displayName: "Menu.SupervisableWorkflows",
            iconName: "supervisable-workflow",
            route: "supervision/supervisable-workflows",
            require: [
              AccessRoleEnum.PlatformAdmin,
              AccessRoleEnum.PlatformSupport,
            ],
          },
          {
            displayName: "Menu.Incidents",
            iconName: "incidents",
            route: "supervision/incidents",
            require: [
              AccessRoleEnum.PlatformAdmin,
              AccessRoleEnum.PlatformSupport,
            ],
          },
        ],
        require: [AccessRoleEnum.PlatformAdmin, AccessRoleEnum.PlatformSupport],
      },
    ];
  }
}
